﻿/*****
    Author:	    CT Software Systems, LLC
    Last Modified: 260408
    */
'use strict';

import {
    isValidImgAsync,
    uploadImgToCloudAsync,
    uploadToImgCtrl
} from './ctssImg.js';
import {
    allSettled,
    getPromise,
    savePromise
} from './ctssJQuery.js';
import {
    getAppToken,
    getImgsBaseURLAsync,
    getImgsFolder,
    getTestFolderParams,
    ImgsFolder
} from './snSite.js';

const testFolderParams = getTestFolderParams();
const token = getAppToken();

const enablePinnedImageBtns = (params = {}) => {
    const { pinnedLimit = 10 } = params;
    const hasImage = $('#imgEntity').prop('src').length > 0;
    if (hasImage) {
        $('#btnPinnedImgAdd').removeAttr('disabled');
        if (pinnedImages.length < pinnedLimit) {
            $('#btnPinImage').removeClass('hidden');
        }
    } else {
        $('#btnPinnedImgAdd').attr('disabled', true);
        $('#btnPinImage').addClass('hidden');
    }
}

const getAsync = async (params = {}) => 
    await getPromise({ url: `PinnedImages/${token.CG}/${params.featureID}` });

const getPinnedImgsBaseURLAsync = async (params) => 
    await getPromise({ url: `PinnedImages/${params.featureID}/ImgsBaseURL` });
    
const getPinnedImageRows = async (params = {}) => {
    const { gridCellClass = 'w150', gridColLimit = 5 } = params;
    let imageCnt = pinnedImages.length;
    
    if (imageCnt === 0) {
        $('#pinnedImagesHeader').addClass('hidden');
        return;
    }
    
    $('#pinnedImagesHeader').removeClass('hidden');

    // ✅ Parallel validation for all images
    const imgValidations = await Promise.all(
        pinnedImages.map(async ({ imgName, id }) => {
            // Try primary URL first
            let src = `${pinnedImgsBaseURL}${imgName}`;
            let isValid = await isValidImgAsync(src);
            
            // Fallback to test folder
            if (!isValid) {
                src = `${testFolderImgsBaseURL}${imgName}`;
                isValid = await isValidImgAsync(src);
            }
            
            return { id, imgName, src: isValid ? src : null };
        }) 
    );

    const validImgs = imgValidations.filter(({ src }) => src !== null);
    imageCnt = validImgs.length;

    // ✅ Build table with consistent column count
    const maxColIdx = gridColLimit - 1;
    let colIdx = 0, content = '';
    
    for (let i = 0; i < imageCnt; i++) {
        if (colIdx === 0) content += '<tr>';
        
        const { id, imgName, src } = validImgs[i];
        
       content += `<td><div class="${gridCellClass}">
            <img id="${id}" class="img-responsive pinImg" 
            data-img_name="${imgName}" src="${src}"/></div></td>`;
        
        // Handle row endings
        if (colIdx === maxColIdx || i === imageCnt - 1) {
            while (colIdx < maxColIdx) {
                content += '<td></td>';
                colIdx++;
            }
            content += '</tr>';
            colIdx = 0;
        } else {
            colIdx++;
        }
    }
    
    $('#gridPinnedImages tbody').html(content);
    
    // ✅ Use event delegation (no setTimeout needed)
    $('#gridPinnedImages').off('click', 'img').on('click', 'img', function () {
        const src = $(this).prop('src');
        const imgNm = $(this).data('img_name');
        
        $('#gridPinnedImages img').removeClass('selectedPin');
        $(this).addClass('selectedPin');
        $('#btnUnpinImage').removeClass('hidden');
        $('#imgEntity').prop('src', src);
        $('#hiddenImgID').val(imgNm);
        $('#btnPinnedImgAdd').removeAttr('disabled');
    });
}

let pinnedImages = [];
let pinnedImgsBaseURL = '';
let testFolderImgsBaseURL = '';


const initPinnedImgsAsync = async (params = {}) => {

    const {
        entityNm,
        featureID,
        gridCellClass,
        gridColLimit,
        pinnedLimit,
        fnPostImgAdd,        
        fnPostImgAddParams  
    } = params;

    if (!entityNm || !featureID) {
        alert('Parameters entityNm, and featureID are required parameters to use initPinnedImgsAsync!');
        return;
    };

    const [pinImagesData, pImgsBaseURL, tImgsBaseURL] = await allSettled({
        promises: [
            getAsync({ featureID }),
            getPinnedImgsBaseURLAsync({ featureID }),
            getImgsBaseURLAsync(ImgsFolder.TestFolder)
        ]
    });

    if (pinImagesData.status === 'rejected' ||
        pImgsBaseURL.status === 'rejected' ||
        tImgsBaseURL.status === 'rejected') {
        return;
    }

    pinnedImages = pinImagesData.value ?? [];
    pinnedImgsBaseURL = pImgsBaseURL.value ?? '';
    testFolderImgsBaseURL = tImgsBaseURL.value ?? '';

    $('#pinnedImagesDlgLbl').text(`Select or click on pinned ${entityNm} image...`);
    $('#lblEntityImg').prop('title', `Select ${entityNm} image...`);

    $('#pinnedImagesDlg').on('show.bs.modal', async () => {
        $('#divImgDlgErrMsg,#btnUnpinImage').addClass('hidden');
        $('#imgEntity').prop('src', '');
        await getPinnedImageRows(params);
    });

    $('#imageDlg').on('show.bs.modal', () => {
        const image = pinnedImages.find(x => x.imgName === $('#hiddenImgID').val());
        $('#imageDlgLbl').text('Unpinning an image...');
        $('#image').off('error').on('error', (e) => {
            e.target.src = `${testFolderImgsBaseURL}${image.imgName}`;
        });
        $('#image').prop('src', `${pinnedImgsBaseURL}${image.imgName}`);
        $('#imagePrompt').text('Unpinning an image cannot be undone.  Are you sure?');
        $('#btnProceed').removeClass('hidden').off().on('click', async () => {
            const id = image.id;
            await unpinAsync({ id });
            pinnedImages.splice(pinnedImages.findIndex(x => x.id === id), 1);
            $('#hiddenImgID').val('');
            $('#imageDlg').modal('hide');
            $('#imgEntity').prop('src', '');
            $('#btnUnpinImage').addClass('hidden');
            await getPinnedImageRows(params);
        });
    });

    $('#editEntityImg').on('change', () => {
        uploadToImgCtrl({
            inputFileCtrl: '#editEntityImg',
            imgCtrl: '#imgEntity',
            divErrMsg: '#divImgDlgErrMsg',
            postOnload: enablePinnedImageBtns,
            postOnloadParams: {}
        });
    });

    $('#btnPinImage').on('click', async () => {

        const newPinnedImage = await pinAsync({ featureID });
        newPinnedImage.displayOrder = pinnedImages.length + 1;
        pinnedImages.push(newPinnedImage);
        $('#hiddenImgID').val(newPinnedImage.id);
        await getPinnedImageRows(params);
        $('#btnPinImage').addClass('hidden');
    });

    $('#btnPinnedImgAdd').on('click', async () => {  
        const postParams = fnPostImgAddParams ?? {};
        postParams.imgSrc = $('#imgEntity').attr('src');
        if (typeof fnPostImgAdd === 'function') {
            await fnPostImgAdd(postParams);
        }
        $('#pinnedImagesDlg').modal('hide');
    });
}

const pinAsync = async (params = {}) => {

    const {
        featureID,
        btnId,
        divErrMsg,
        loaderId
    } = params;

    if (!featureID) {
        alert('FeatureID is a required parameter for snPinnedImages.pinAsync');
    }

    let pinnedImage;
    await uploadImgToCloudAsync({
        hasParamsStorage: true,
        imgsFolder: getImgsFolder({ featureID }),
        imgCtrl: '#imgEntity',
        isDefaultImg: false,
        url: 'Storage/Img',
        useTestFolder: testFolderParams.useTestFolder,
        btnId,
        divErrMsg,
        postOnload: async (imgName) => {
            pinnedImage = await savePromise({
                url: 'PinnedImages/Pin',
                data: { cg: `${token.CG}`, featureID, imgName },
                btnId,
                loaderId
            }).then((id) => { return { id, imgName }; });
        } 
    });
    return pinnedImage;
}

const unpinAsync = async (params = {}) => {
    const { id, btnId, loaderId } = params;
    if (!id) {
        alert('id is a required parameter for snPinnedImages.unpinAsync');
        return;
    };
    await savePromise({
        url: 'PinnedImages/Unpin',
        data: { cg: `${token.CG}`, id: id.toString() },
        btnId,
        loaderId
    });
}

        
export {
    getAsync,
    getImgsBaseURLAsync,
    initPinnedImgsAsync,
    pinAsync,
    unpinAsync
}; 
